/**
 * Dashboard state, built from `PlatformEvent`s alone. Every event carries the full current record, so the reducer
 * is an upsert by id. The same reducer runs for mock, live and replay, which is what makes a recording replay
 * exactly as it ran.
 */
import type {
  AgentRun,
  CoordinatorSnapshot,
  DeployRecord,
  DiffSummary,
  IntentClaim,
  LandRequest,
  Message,
  MigrationLane,
  Overlap,
  PlatformEvent,
  Resolution,
  Session,
  Verdict,
  WorkPackage,
  WorkPlan,
} from "@botallion/contracts";

export interface DashboardState {
  sessions: Record<string, Session>;
  claims: Record<string, IntentClaim>;
  overlaps: Record<string, Overlap>;
  /** In the order the coordinator accepted them. */
  messages: Message[];
  /** In the order recorded. */
  resolutions: Resolution[];
  plans: Record<string, WorkPlan>;
  /** Latest version of every package, by WP id. Newer than the copy inside its plan. */
  packages: Record<string, WorkPackage>;
  /** In the order recorded. */
  verdicts: Verdict[];
  lands: Record<string, LandRequest>;
  deploys: Record<string, DeployRecord>;
  /** Agent runs as their runner last reported them (WP-17), by run id. A run can exist before its session and after it. */
  runs: Record<string, AgentRun>;
  diffs: DiffSummary[];
  lane: MigrationLane;
  /** Each claim's lease length, from its declaration. Heartbeats renew leases without a claim event. */
  claimLeaseMs: Record<string, number>;
  /** Seq of the last event applied. 0 before any. */
  lastSeq: number;
  /** Timestamp of the last event applied. */
  lastTs: string | null;
  /** Every event applied, oldest first. Kept so a run can be saved and replayed. */
  log: PlatformEvent[];
}

export function emptyState(): DashboardState {
  return {
    sessions: {},
    claims: {},
    overlaps: {},
    messages: [],
    resolutions: [],
    plans: {},
    packages: {},
    verdicts: [],
    lands: {},
    deploys: {},
    runs: {},
    diffs: [],
    lane: { holder: null, queue: [] },
    claimLeaseMs: {},
    lastSeq: 0,
    lastTs: null,
    log: [],
  };
}

/** Apply one event in place. Events at or below `lastSeq` are ignored, so a reconnect that repeats some is harmless. */
export function applyEvent(state: DashboardState, event: PlatformEvent): DashboardState {
  if (event.seq <= state.lastSeq) return state;
  state.lastSeq = event.seq;
  state.lastTs = event.ts;
  state.log.push(event);
  switch (event.type) {
    case "session.registered":
    case "session.updated":
      state.sessions[event.data.session.id] = event.data.session;
      break;
    case "session.heartbeat": {
      const s = state.sessions[event.data.sessionId];
      if (s) state.sessions[s.id] = { ...s, lastHeartbeat: event.data.lastHeartbeat, note: event.data.note };
      // Same rule as the coordinator: a heartbeat renews each live claim's lease, never shortens it.
      const now = Date.parse(event.data.lastHeartbeat);
      for (const c of Object.values(state.claims)) {
        if (c.sessionId !== event.data.sessionId || (c.status !== "active" && c.status !== "queued")) continue;
        const lease = state.claimLeaseMs[c.id];
        if (lease !== undefined && now + lease > Date.parse(c.expiresAt)) {
          state.claims[c.id] = { ...c, expiresAt: new Date(now + lease).toISOString() };
        }
      }
      break;
    }
    case "claim.declared":
      state.claimLeaseMs[event.data.claim.id] = Date.parse(event.data.claim.expiresAt) - Date.parse(event.data.claim.createdAt);
      state.claims[event.data.claim.id] = event.data.claim;
      break;
    case "claim.updated":
      state.claims[event.data.claim.id] = event.data.claim;
      break;
    case "overlap.detected":
    case "overlap.updated":
      state.overlaps[event.data.overlap.id] = event.data.overlap;
      break;
    case "message.posted":
      upsertInOrder(state.messages, event.data.message);
      break;
    case "resolution.recorded":
      upsertInOrder(state.resolutions, event.data.resolution);
      break;
    case "migration_lane.updated":
      state.lane = event.data.lane;
      break;
    case "plan.submitted":
    case "plan.approved":
    case "plan.amended":
      state.plans[event.data.plan.specId] = event.data.plan;
      for (const p of event.data.plan.packages) state.packages[p.id] = p;
      break;
    case "package.updated":
      state.packages[event.data.package.id] = event.data.package;
      break;
    case "diff.ingested":
      state.diffs.push(event.data.diff);
      break;
    case "verdict.recorded":
      state.verdicts.push(event.data.verdict);
      break;
    case "land.updated":
      state.lands[event.data.land.id] = event.data.land;
      break;
    case "deploy.updated":
      state.deploys[event.data.deploy.id] = event.data.deploy;
      break;
    case "run.updated":
      state.runs[event.data.run.id] = event.data.run;
      break;
  }
  return state;
}

function upsertInOrder<T extends { id: string }>(list: T[], item: T): void {
  const i = list.findIndex((x) => x.id === item.id);
  if (i >= 0) list[i] = item;
  else list.push(item);
}

/** Build state from a whole log. */
export function stateFrom(events: readonly PlatformEvent[]): DashboardState {
  const state = emptyState();
  for (const e of events) applyEvent(state, e);
  return state;
}

/** A plan with its packages replaced by their latest versions. */
export function currentPlan(state: DashboardState, plan: WorkPlan): WorkPlan {
  return { ...plan, packages: plan.packages.map((p) => state.packages[p.id] ?? p) };
}

/**
 * The state in the coordinator's snapshot shape. The tests compare it with the coordinator's own snapshot after the
 * same events, which proves the dashboard shows exactly what the coordinator holds.
 */
export function toSnapshot(state: DashboardState): CoordinatorSnapshot {
  return {
    sessions: Object.values(state.sessions),
    claims: Object.values(state.claims),
    overlaps: Object.values(state.overlaps),
    messages: [...state.messages],
    resolutions: [...state.resolutions],
    plans: Object.values(state.plans).map((p) => currentPlan(state, p)),
    verdicts: [...state.verdicts],
    lands: Object.values(state.lands),
    deploys: Object.values(state.deploys),
    runs: Object.values(state.runs),
    migrationLane: state.lane,
    lastSeq: state.lastSeq,
  };
}
